import { memo, useCallback } from 'react';
import { Button, ButtonGroup, Box } from '@mui/material';

export interface ChoiceOption {
  value: number;
  label: string;
}

interface SurveyChoiceButtonsProps {
  options: readonly ChoiceOption[];
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
}

// Static sx objects moved outside component to prevent recreation
const containerSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
} as const;

const buttonGroupSx = {
  flexWrap: { xs: 'wrap', sm: 'nowrap' },
  gap: { xs: 1, sm: 0 },
  '& .MuiButton-root': {
    // iPad-optimized touch targets
    minHeight: { xs: 48, sm: 64, md: 72 },
    fontSize: {
      xs: '0.9rem',
      sm: '1.2rem',
      md: '1.3rem',
      lg: '1.15rem',
    },
    px: { xs: 2, sm: 4, md: 5 },
    py: { xs: 1.5, sm: 2.5, md: 3 },
    fontWeight: 600,
    borderWidth: 2,
    // Removed expensive transition and hover transforms
    '&.MuiButton-contained': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
  },
} as const;

// Memoized individual button component to prevent re-renders
const ChoiceButton = memo(function ChoiceButton({
  option,
  isSelected,
  onClick,
  disabled,
}: {
  option: ChoiceOption;
  isSelected: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      onClick={onClick}
      variant={isSelected ? 'contained' : 'outlined'}
      disabled={disabled}
    >
      {option.label}
    </Button>
  );
});

const SurveyChoiceButtons = memo(function SurveyChoiceButtons({
  options,
  value,
  onChange,
  disabled = false,
}: SurveyChoiceButtonsProps) {
  // Create stable click handlers for each option
  const handleClick = useCallback(
    (optionValue: number) => {
      onChange(optionValue);
    },
    [onChange],
  );

  return (
    <Box sx={containerSx}>
      <ButtonGroup size="large" orientation="horizontal" sx={buttonGroupSx}>
        {options.map((option) => (
          <ChoiceButton
            key={option.value}
            option={option}
            isSelected={value === option.value}
            onClick={() => handleClick(option.value)}
            disabled={disabled}
          />
        ))}
      </ButtonGroup>
    </Box>
  );
});

export default SurveyChoiceButtons;
